import { useMemo } from 'react'
import { useParams } from 'react-router-dom'

import { useTerritoryQueries } from 'hooks-queries/territory'

import { GET_SCOPE_TERRITORY_ERROR_LOG } from './niche-scope.consts'
import { NicheScopeContext } from './niche-scope.context'
import type { INicheScopeProviderProps, IScopedRouteParams, TUseNicheScopeReturn } from './niche-scope.types'

/**
 * Resolves the current Promoted-Niche scope from the `:categoryId` URL segment
 * and the `getScopeTerritory` query, and shares it via context.
 *
 * Mount this ONCE at the top of the scoped Niche Territory route — above
 * `TerritoryPage`'s nested tab `<Switch>`. `useParams` only exposes
 * `:categoryId` at the level that matched the route pattern; the tab `<Switch>`
 * re-matches on a concrete pathname (no `:params`), so any `useParams` call
 * inside it returns `{}`. Reading the scope from this provider instead keeps it
 * stable for every descendant — overview cards, tab pages, breadcrumb and
 * copy-query surfaces alike.
 *
 * Off the scoped route the provider is simply not mounted, so `useNicheScope`
 * returns its all-`null` fallback and the underlying query never runs.
 */
export function NicheScopeProvider({ children }: INicheScopeProviderProps) {
  const { categoryId } = useParams<IScopedRouteParams>()
  const scopeId = categoryId ? `industry_topic.${categoryId}` : null

  const { data, loading } = useTerritoryQueries({
    queryName: 'getScopeTerritory',
    queryVariables: { territoryId: scopeId ?? '' },
    errorLog: GET_SCOPE_TERRITORY_ERROR_LOG,
    options: {
      skip: !scopeId,
    },
  })

  const value = useMemo<TUseNicheScopeReturn>(() => {
    if (!scopeId) {
      return {
        categoryId: null,
        scopeId: null,
        territoryIds: null,
        scopeQuery: null,
        scopeName: null,
        isLoading: false,
      }
    }

    const territory = data?.getTerritory
    const industryTopic = territory?.__typename === 'IndustryTopic' ? territory : null

    return {
      categoryId: categoryId ?? null,
      scopeId,
      territoryIds: [scopeId],
      scopeQuery: industryTopic?.territoryQuery ?? null,
      scopeName: industryTopic?.name ?? null,
      isLoading: loading,
    }
  }, [scopeId, categoryId, data, loading])

  return <NicheScopeContext.Provider value={value}>{children}</NicheScopeContext.Provider>
}
